import { useMemo, type ReactElement } from 'react';
import { usePageBreadcrumb, usePageSidebar, useStrapiApi, useStrapiParams } from '@/hooks';
import { sidebarItems } from './sidebar';
import { useParams } from 'react-router-dom';
import type { ProductCatalog, StrapiListRequest, StrapiResponse } from '@/interfaces/strapi';
import { combinePhone, type BookItem, type CatalogRequestFormValues } from '@/components/CatalogRequestStepper/config';
import { createProductRequest, getProductBySlug } from '@/services/strapi';
import { ROUTES } from '@/constants';
import { CatalogRequestStepper, InfoContacto, SectionIntro, SectionTitle } from '@/components';
import { type NotificationBasicConfig } from '@/interfaces';
import { useNotification } from '@/context';
import { notifications } from '@/utils/notifications';

export const CatalogoSolicitud = (): ReactElement => {
	const { category: categorySlug, product: productSlug } = useParams<{ category: string; product: string }>();
	const { notificationSuccess, notificationError } = useNotification();
	const params = useStrapiParams({
		page: 1,
		pageSize: 1,
		additionalFilters: productSlug ? { slug: { $eq: productSlug } } : {},
		populate: {
			image: true,
			categoria: true,
		},
	});

	const { data: productResponse, loading: loadingProduct } = useStrapiApi<
		StrapiResponse<ProductCatalog>,
		StrapiListRequest
	>(getProductBySlug, {
		autoFetch: true,
		params,
	});

	const { loading: loadingCreate, fetch: create } = useStrapiApi(createProductRequest, {
		autoFetch: false,
		onSuccess: () =>
			handleSuccess(
				notifications({
					title: 'La solicitud se ha creado correctamente',
					content: '',
					btn: { accept: 'Aceptar' },
				}).success,
			),
		onError: () =>
			handleError(
				notifications({
					title: 'Error al crear la solicitud',
					content: '',
					btn: { accept: 'Aceptar' },
				}).error,
			),
	});

	const product = productResponse?.data;

	const postProductRequest = (values: CatalogRequestFormValues) => {
		create({
			name: values.name,
			nif: values.nif,
			email: values.email,
			phone: combinePhone(values.movil),
			address: values.direccion,
			addressFactura: values.direccionFacturacion ?? '',
			productos: values.libros.map((book: BookItem) => String(book.id)),
			acceptedPolicy: Boolean(values.acceptPolicy),
		});
	};

	const handleSuccess = (message: NotificationBasicConfig) => {
		notificationSuccess(message);
	};

	const handleError = (message: NotificationBasicConfig) => {
		notificationError(message);
	};

	usePageBreadcrumb([
		{ label: 'Academia Matritense', to: ROUTES.ACADEMIA_MATRITENSE_HISTORIA },
		{ label: 'Catálogo', to: ROUTES.ACADEMIA_MATRITENSE_CATALOGO },
		{ label: 'Solicitar' },
	]);

	const sidebar = useMemo(() => sidebarItems, []);
	usePageSidebar(sidebar, true);

	return (
		<div>
			<SectionTitle title="Solicitar Libros" tag="h1" align="left" className="u-mb-16" />
			<SectionIntro text="Una vez cumplimentado el presente formulario y a la mayor brevedad se le enviará al email por ud. facilitado una factura pro-forma para que pueda realizar el abono del (los) libro (s) adquiridos. Los libros se envían por mensajería." />
			<InfoContacto email="academia@madrid.notariado.org" phone="91 213 00 14" />
			{product && <CatalogRequestStepper defaultProduct={product} onSubmitSuccess={postProductRequest} />}
		</div>
	);
};
